import Drawer from "@mui/material/Drawer";
import { Box, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { useInteractionMetadata } from "../../hooks/useInteraction";
import { useCallback, useContext, useEffect } from "react";
import {
  canTranscribe,
  Codec,
  InteractionType,
  normalizeMediaType,
} from "../../types/interaction/Interaction";
import FieldLabelContext from "../../pages/search/context/FieldLabelContext";
import MediaDisplaySelector from "./MediaDisplaySelector";
import ClosePlayerButton from "./ButtonsAndControls/ClosePlayerButton";
import LoadingIndicator from "../common/LoadingIndicator/LoadingIndicator";
import { useFileWithTranscodePolling } from "../../hooks/useTranscoding";
import { TranscriptionView } from "../transcription/TranscriptionView";
import { TranscriptionOptionsMenu } from "../transcription/TranscriptionOptionsMenu";
import { SummaryView } from "../summary/SummaryView";
import { HideBehindFeature } from "../common/FeatureFlags/HideBehindFeature";
import { FeatureFlagName } from "../../types/feature-flags/featureFlag";
import { useFeatureFlagToken } from "../../hooks/useFeatureFlag";
import { PlaybackTimeProvider } from "../../context/PlaybackTimeContext";
import NewWindowButton from "./ButtonsAndControls/NewWindowButton";
import { useAlert } from "../../context/AlertContext";
import { useTranslation } from "react-i18next";

export const getDefaultVolume = (defaultVolume: number): number => {
  const volume = localStorage.getItem("preferredVolume");
  if (volume) {
    return Number(volume);
  }
  return defaultVolume;
};

interface MediaDrawerProps {
  mediaDrawerOpen: boolean;
  playerInteractionId: string;
  customerId: string;
  closePlayer: () => void;
  isPlaying: boolean;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  mediaDrawerWidth: number;
  setMediaDrawerWidth: React.Dispatch<React.SetStateAction<number>>;
  minMediaDrawerWidth: number;
  maxMediaDrawerWidth: number;
  mediaType: string | undefined;
}

export const MediaDrawer = ({
  mediaDrawerOpen,
  playerInteractionId,
  customerId,
  closePlayer,
  isPlaying,
  setIsPlaying,
  mediaDrawerWidth,
  minMediaDrawerWidth,
  maxMediaDrawerWidth,
  setMediaDrawerWidth,
  mediaType,
}: MediaDrawerProps) => {
  const { t } = useTranslation();
  const isSummarizationEnabled = useFeatureFlagToken(FeatureFlagName.Summarization);
  const {
    data,
    error,
    isLoading,
    isPolling,
    isTranscoding,
    isRedacting,
    isMissingRedactedTranscript,
    isFileNotInExpectedLocation,
    errorMessage,
  } = useFileWithTranscodePolling(playerInteractionId, customerId);
  const showAlert = useAlert();

  useEffect(() => {
    if (!error) {
      return;
    }
    console.error("Error fetching media file:", error);
    showAlert(t("errors:mediaFetchError"), "error");
  }, [error, showAlert, t]);

  const { data: metadata } = useInteractionMetadata(
    customerId!,
    playerInteractionId!,
  );
  const agentName = (metadata as { agentName?: string })?.agentName;
  const phoneNumber = (metadata as { phoneNumber?: string })?.phoneNumber;
  const startTime = (metadata as { startTime?: string })?.startTime;
  const endTime = (metadata as { endTime?: string })?.endTime;

  const { getFieldLabel } = useContext(FieldLabelContext);

  const handleMouseDown = () => {
    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseUp = () => {
    document.removeEventListener("mouseup", handleMouseUp, true);
    document.removeEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const newWidth =
        document.body.offsetLeft + document.body.offsetWidth - e.clientX + 20;

      if (newWidth >= minMediaDrawerWidth && newWidth <= maxMediaDrawerWidth) {
        setMediaDrawerWidth(newWidth);
        localStorage.setItem("prefMediaPlayerWidth", newWidth.toString());
      }
    },
    [minMediaDrawerWidth, maxMediaDrawerWidth, setMediaDrawerWidth],
  );

  const handleOpenInNewWindow = (playerInteractionId: string) => {
    const newWindowUrl = `/customers/${customerId}/interactions/${playerInteractionId}/${mediaType}/${getFieldLabel(
      "sourceId",
    )}/${getFieldLabel("startTime")}/${getFieldLabel("endTime")}`;
    const uniqueName = `Playing-Interaction-${playerInteractionId}`;
    const windowFeatures =
      "width=700,height=800,menubar=no,toolbar=no,location=no,fullscreen=yes, status=no,scrollbars=yes,resizable=yes rel=noopener no referrer";
    window.open(
      window.location.origin + newWindowUrl,
      uniqueName,
      windowFeatures,
    );
    if (window) {
      window.focus();
    }
    if (closePlayer) closePlayer();
  };

  const normalizedType = normalizeMediaType(mediaType!) as InteractionType;

  return (
    <Box
      sx={{
        display: "flex",
        overflow: "hidden",
        alignContent: "space-between",
      }}
    >
      <Drawer
        sx={{
          zIndex: 1050,
          display: "flex",
          width: mediaDrawerWidth,
          justifyItems: "center",
          justifyContent: "center",
          alignContent: "space-between",
          height: "100%",
          overflowY: "auto",
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: mediaDrawerWidth,
            boxSizing: "border-box",
            p: 3,
            paddingTop: 8,
          },
        }}
        variant="persistent"
        anchor="right"
        open={mediaDrawerOpen}
      >
        <PlaybackTimeProvider>
          <Tooltip title="Drag to resize" followCursor>
            <Box
              onMouseDown={() => handleMouseDown()}
              sx={{
                width: "8px",
                cursor: "grab",
                padding: "4px 0 0",
                borderTop: "1px solid #ddd",
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                zIndex: 100,
                backgroundColor: "#f4f7f9",
                boxShadow: 5,
                userSelect: "none",
              }}
            />
          </Tooltip>
          <Box
            className="flex flex-col"
            sx={{
              flex: 1,
              overflowY: "auto",
              padding: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography
                fontWeight={"bold"}
                variant="h6"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {agentName} {phoneNumber}
              </Typography>
              <Box sx={{ flexShrink: 0 }}>
                {normalizedType === InteractionType.Chat ||
                normalizedType === InteractionType.Email ? (
                  <NewWindowButton
                    playerInteractionId={playerInteractionId}
                    handleOpenInNewWindow={handleOpenInNewWindow}
                    isLoading={isLoading}
                  />
                ) : null}
                {canTranscribe(
                  metadata?.codec as Codec | undefined,
                  normalizedType,
                  Boolean(metadata?.hasMedia),
                ) && (
                  <TranscriptionOptionsMenu
                    customerId={customerId}
                    interactionId={playerInteractionId}
                    hasExistingTranscript={Boolean(
                      (
                        metadata as {
                          transcriptPath?: string;
                          redactedTranscriptPath?: string;
                        }
                      )?.transcriptPath ||
                      (
                        metadata as {
                          transcriptPath?: string;
                          redactedTranscriptPath?: string;
                        }
                      )?.redactedTranscriptPath,
                    )}
                  />
                )}
                <ClosePlayerButton closePlayer={closePlayer} />
              </Box>
            </Box>

            <Box className="w-full flex flex-wrap pb-2">
              <Typography variant="subtitle2" color="gray">
                {startTime && new Date(startTime).toLocaleString()} {" - "}
                {endTime && new Date(endTime).toLocaleString()}
              </Typography>
            </Box>

            {![InteractionType.Chat, InteractionType.Email].includes(
              normalizedType,
            ) &&
              isMissingRedactedTranscript && <NoRedactedTranscriptError />}

            {isFileNotInExpectedLocation && (
              <MediaFileNotFoundError message={errorMessage} />
            )}

            {isLoading ? (
              <Box sx={{ mt: 3 }}>
                <LoadingIndicator />
              </Box>
            ) : isPolling ? (
              <Box sx={{ mt: 3 }}>
                <ProcessingInteraction
                  isTranscoding={isTranscoding}
                  isRedacting={isRedacting}
                />
              </Box>
            ) : (
              <MediaDisplaySelector
                interactionType={normalizedType}
                playerInteractionId={playerInteractionId}
                data={data}
                customerId={customerId}
                isLoading={isLoading}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                mediaDrawerWidth={mediaDrawerWidth}
                handleOpenInNewWindow={handleOpenInNewWindow}
                metadata={metadata}
              />
            )}
            {(() => {
              const hasTranscript = Boolean(
                (metadata as { transcriptPath?: string })?.transcriptPath ||
                (metadata as { redactedTranscriptPath?: string })
                  ?.redactedTranscriptPath,
              );
              return (
                <>
                  <HideBehindFeature name={FeatureFlagName.Summarization}>
                    <SummaryView
                      className="mt-4"
                      customerId={customerId}
                      interactionId={playerInteractionId}
                      hasExistingTranscript={hasTranscript}
                    />
                  </HideBehindFeature>
                  {(hasTranscript ||
                    (!isSummarizationEnabled &&
                      canTranscribe(
                        metadata?.codec as Codec | undefined,
                        normalizedType,
                        Boolean(metadata?.hasMedia),
                      ))) && (
                    <TranscriptionView
                      className="mt-4"
                      customerId={customerId}
                      interactionId={playerInteractionId}
                      hasExistingTranscript={hasTranscript}
                    />
                  )}
                </>
              );
            })()}
          </Box>
        </PlaybackTimeProvider>
      </Drawer>
    </Box>
  );
};

/**
 * Displays a little progress bar when transcoding or redacting
 */
function ProcessingInteraction({
  isTranscoding,
  isRedacting,
}: {
  isTranscoding: boolean;
  isRedacting: boolean;
}) {
  let message = "Processing in progress...";
  if (isTranscoding) {
    message = "Transcoding in progress...";
  } else if (isRedacting) {
    message = "Redaction in progress...";
  }

  return (
    <Stack spacing={2} alignItems="center" width="100%">
      <Typography variant="h6" color="text.secondary">
        {message}
      </Typography>
      <Box sx={{ width: "100%" }}>
        <LinearProgress />
      </Box>
    </Stack>
  );
}

/**
 * Displays an inline error banner when the media file is not at its expected location
 */
function MediaFileNotFoundError({ message }: { message?: string }) {
  return (
    <Box
      sx={{
        backgroundColor: "#f8d7da",
        border: "1px solid #f5c2c7",
        borderRadius: 1,
        padding: 2,
        marginBottom: 2,
      }}
    >
      <Typography variant="body1" color="#842029">
        {message ??
          "Media file not found at expected location. Please contact support."}
      </Typography>
    </Box>
  );
}

/**
 * Displays an inline error banner when redacted media requires a transcript
 */
function NoRedactedTranscriptError() {
  return (
    <Box
      sx={{
        backgroundColor: "#fff3cd",
        border: "1px solid #ffc107",
        borderRadius: 1,
        padding: 2,
        marginBottom: 2,
      }}
    >
      <Typography variant="body1" color="#856404">
        A redacted transcript must be generated before playback can begin.
        Please generate a redacted transcript or have someone with the
        appropriate permissions generate one for you.
      </Typography>
    </Box>
  );
}
